import { Add, ConfirmationNumber, ErrorOutline } from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material'
import { green, grey, orange } from '@mui/material/colors'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import useCustomerTickets from '../../hooks/api/useCustomerTickets'
import useIdentities from '../../hooks/api/useIdentities'
import useOtherAvatar from '../../hooks/api/useOtherAvatar'
import useAlert from '../../hooks/useAlert'
import { updateObjectAtIndex } from '../../utils/array'
import { getIdentityInitials, getIdentityName } from '../../utils/identity'
import EditTicketModal from '../components/Ticket/EditTicketModal'
import CustomerHeader from '../components/CustomerHeader'
import Tooltip from '../../common/Tooltip'

const STATUS_COLORS = {
  1: { color: orange[100], label: 'New', hasIcon: true },
  2: { color: green[100], label: 'In Progress' },
  3: { color: green[300], label: 'Completed' },
}

const Tickets = ({
  id,
  dropdowns,
  customerQuery,
  customerStatusTypesData,
  types,
  customerTicketsQuery,
}) => {
  const {
    tickets,
    setTickets,
    isLoading,
    hasLoadError,
    addNote,
    editNote,
    removeNote,
  } = customerTicketsQuery

  const { identities } = useIdentities()
  const avatarsNeeded = [
    ...new Set(tickets.map((t) => t.assigneeIdentityId).filter((id) => !!id)),
  ]

  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const { avatarUrlsMap } = useOtherAvatar(avatarsNeeded)

  const [isCreatingTicket, setIsCreatingTicket] = useState(false)

  const { createAlert } = useAlert()

  const selectedTicketId = params.get('ticketId')

  const selectedTicket =
    selectedTicketId && selectedTicketId !== 'new'
      ? tickets.find((t) => t.id === Number(selectedTicketId))
      : null

  useEffect(() => {
    if (
      selectedTicketId !== 'new' &&
      tickets.length > 0 &&
      selectedTicketId &&
      !selectedTicket
    ) {
      params.delete('ticketId')
      setParams(params)
    }
  }, [selectedTicket, selectedTicketId, tickets])

  if (isLoading) return <CircularProgress />

  if (hasLoadError)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Alert severity='error'>Failed to load tickets for this customer</Alert>
      </Box>
    )

  const getTicketStatus = (statusTypeId) => {
    const { color, label, hasIcon } = STATUS_COLORS[statusTypeId]
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 'fit-content',
          mr: 1,
          py: 1,
          px: 2,
          borderRadius: '6px',
          backgroundColor: color,
        }}
      >
        {label}
        {hasIcon && <ErrorOutline sx={{ ml: 0.5 }} />}
      </Box>
    )
  }

  const handleSelectTicket = (id) => {
    params.set('ticketId', `${id}`)
    setParams(params)
  }

  const handleCloseModal = () => {
    setIsCreatingTicket(false)
    params.delete('ticketId')

    setParams(params)
  }

  const updateTicket = (ticket) => {
    const index = tickets.map((t) => t.id).indexOf(ticket.id)

    if (index !== -1) setTickets(updateObjectAtIndex(tickets, ticket, index))
    else setTickets([ticket].concat(tickets))
  }

  const handleSubmitModal = (ticket) => {
    updateTicket(ticket)
    handleCloseModal()
    createAlert({ message: `Successfully saved ticket #${ticket.id}` })
  }

  const openTickets = tickets.filter((t) => t.statusTypeId !== 3 && !t.closedOn)

  return (
    <>
      <CustomerHeader
        customer={customerQuery.customer}
        customerStatusTypesData={customerStatusTypesData}
        customerTicketsQuery={customerTicketsQuery}
        types={types}
        Icon={ConfirmationNumber}
        tooltip={
          <Tooltip
            infoText={
              'In this section, you have the ability to create, categorize, and assign users to monitor any outstanding support tickets linked to the current account profile.'
            }
          />
        }
      />
      <Box>
        <EditTicketModal
          importanceTypes={types?.importanceTypes || []}
          isCreatingTicket={isCreatingTicket || selectedTicketId === 'new'}
          onClose={() => handleCloseModal()}
          onSave={handleSubmitModal}
          updateTicket={updateTicket}
          identities={identities}
          ticket={selectedTicket}
          avatarUrlsMap={avatarUrlsMap}
          getTicketStatus={getTicketStatus}
          dropdowns={dropdowns}
          addNote={addNote}
          editNote={(note) => editNote(selectedTicket.id, note)}
          customerQuery={customerQuery}
          removeNote={(note) => removeNote(selectedTicket.id, note)}
        ></EditTicketModal>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            mb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '18px', ml: 1 }}>
            {openTickets.length > 0 &&
              openTickets.length +
                ' Open Ticket' +
                (openTickets.length > 1 ? 's' : '')}
          </Typography>
          <Button onClick={() => setIsCreatingTicket(true)} startIcon={<Add />}>
            Create a ticket
          </Button>
        </Box>

        {tickets.map((t) => {
          const assignee = identities.find((i) => i.id === t.assigneeIdentityId)
          const avatarUrl = avatarUrlsMap[t.assigneeIdentityId]

          const assigneeName = assignee ? getIdentityName(assignee) : null

          return (
            <Card
              sx={{
                cursor: 'pointer',
                mb: 1,
                '&:hover': {
                  backgroundColor: grey['50'],
                },
              }}
              key={t.id}
              onClick={() => handleSelectTicket(t.id)}
            >
              <CardContent sx={{ pb: '16px !important' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    '& > *': {
                      width: '33%',
                      minWidth: '200px',
                      my: 1,
                    },
                  }}
                >
                  <Box>
                    <Typography>
                      <b>Ticket #:</b> {t.id}
                    </Typography>

                    <Typography>
                      <b>Category:</b> {t.categoryName}
                    </Typography>
                    <Typography>
                      <b>Description:</b> {t.issueDescription}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box>
                      <Typography>
                        Created on: {new Date(t.createdOn).toLocaleDateString()}
                      </Typography>

                      {t.createdOn !== t.modifiedOn && (
                        <Typography>
                          Last updated:{' '}
                          {new Date(t.modifiedOn).toLocaleDateString()}
                        </Typography>
                      )}
                      {t.closedOn && (
                        <Typography>
                          Completed on:{' '}
                          {new Date(t.closedOn).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {getTicketStatus(t.statusTypeId)}
                    {(avatarUrl || assignee) && (
                      <Avatar src={avatarUrl} alt={assigneeName}>
                        {assignee ? getIdentityInitials(assignee) : null}
                      </Avatar>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )
        })}
      </Box>
    </>
  )
}

export default Tickets
